/**
 * BIRHAUS v3.0 Typography System
 * Clean, modern, highly readable typefaces with generous spacing
 */

export const typographyV3 = {
  // Font families - System-first approach with fallbacks
  fontFamily: {
    // Primary sans-serif stack
    sans: [
      'Inter',
      'SF Pro Display',
      '-apple-system',
      'BlinkMacSystemFont',
      'system-ui',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'Noto Sans',
      'sans-serif',
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
      'Noto Color Emoji'
    ],
    
    // Monospace for code
    mono: [
      'JetBrains Mono',
      'SF Mono',
      'Monaco',
      'Consolas',
      'Liberation Mono',
      'Menlo',
      'Courier New',
      'monospace'
    ],
    
    // Display font for headings and emphasis
    display: [
      'Satoshi',
      'Cal Sans',
      'Inter',
      'SF Pro Display',
      '-apple-system',
      'BlinkMacSystemFont',
      'system-ui',
      'sans-serif'
    ],
    
    // Serif for special occasions
    serif: [
      'New York',
      'SF Pro Serif',
      'Georgia',
      'Times New Roman',
      'serif'
    ],
  },
  
  // Font sizes with generous line heights for readability
  fontSize: {
    xs: { 
      size: '13px', 
      lineHeight: '20px',
      letterSpacing: '0.01em'
    },
    sm: { 
      size: '15px', 
      lineHeight: '24px',
      letterSpacing: '0.01em'
    },
    base: { 
      size: '17px',      // Larger base for better readability
      lineHeight: '28px', // Generous line height
      letterSpacing: '0'
    },
    lg: { 
      size: '19px', 
      lineHeight: '32px',
      letterSpacing: '-0.01em'
    },
    xl: { 
      size: '23px', 
      lineHeight: '36px',
      letterSpacing: '-0.02em'
    },
    '2xl': { 
      size: '28px', 
      lineHeight: '40px',
      letterSpacing: '-0.02em'
    },
    '3xl': { 
      size: '36px', 
      lineHeight: '48px',
      letterSpacing: '-0.03em'
    },
    '4xl': { 
      size: '48px', 
      lineHeight: '60px',
      letterSpacing: '-0.03em'
    },
    '5xl': { 
      size: '64px', 
      lineHeight: '76px',
      letterSpacing: '-0.04em'
    },
    '6xl': { 
      size: '80px', 
      lineHeight: '92px',
      letterSpacing: '-0.04em'
    },
    '7xl': { 
      size: '96px', 
      lineHeight: '108px',
      letterSpacing: '-0.05em'
    },
  },
  
  // Font weights
  fontWeight: {
    thin: '100',
    extralight: '200',
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
    black: '900',
  },
  
  // Letter spacing scale
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.03em',
    normal: '0',
    wide: '0.02em',
    wider: '0.04em',
    widest: '0.08em',
  },
  
  // Line height scale
  lineHeight: {
    none: '1',
    tight: '1.25',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.625',
    loose: '2',
    generous: '2.25',
  },
} as const

// Semantic type scales for different purposes
export const typeScales = {
  // UI text scale (buttons, labels, etc.)
  ui: {
    xs: typographyV3.fontSize.xs,
    sm: typographyV3.fontSize.sm,
    base: typographyV3.fontSize.base,
    lg: typographyV3.fontSize.lg,
    xl: typographyV3.fontSize.xl,
  },
  
  // Content text scale (body copy, descriptions)
  content: {
    sm: { ...typographyV3.fontSize.sm, lineHeight: '26px' },
    base: { ...typographyV3.fontSize.base, lineHeight: '30px' },
    lg: { ...typographyV3.fontSize.lg, lineHeight: '34px' },
    xl: { ...typographyV3.fontSize.xl, lineHeight: '38px' },
  },
  
  // Heading scale with display characteristics
  heading: {
    h6: { ...typographyV3.fontSize.lg, fontWeight: typographyV3.fontWeight.semibold },
    h5: { ...typographyV3.fontSize.xl, fontWeight: typographyV3.fontWeight.semibold },
    h4: { ...typographyV3.fontSize['2xl'], fontWeight: typographyV3.fontWeight.semibold },
    h3: { ...typographyV3.fontSize['3xl'], fontWeight: typographyV3.fontWeight.bold },
    h2: { ...typographyV3.fontSize['4xl'], fontWeight: typographyV3.fontWeight.bold },
    h1: { ...typographyV3.fontSize['5xl'], fontWeight: typographyV3.fontWeight.bold },
  },
  
  // Display scale for heroes and emphasis
  display: {
    sm: { ...typographyV3.fontSize['4xl'], fontWeight: typographyV3.fontWeight.bold },
    md: { ...typographyV3.fontSize['5xl'], fontWeight: typographyV3.fontWeight.bold },
    lg: { ...typographyV3.fontSize['6xl'], fontWeight: typographyV3.fontWeight.bold },
    xl: { ...typographyV3.fontSize['7xl'], fontWeight: typographyV3.fontWeight.extrabold },
  },
} as const

// Component-specific typography
export const componentTypography = {
  // Button text
  button: {
    xs: { ...typographyV3.fontSize.xs, fontWeight: typographyV3.fontWeight.medium },
    sm: { ...typographyV3.fontSize.sm, fontWeight: typographyV3.fontWeight.medium },
    base: { ...typographyV3.fontSize.base, fontWeight: typographyV3.fontWeight.medium },
    lg: { ...typographyV3.fontSize.lg, fontWeight: typographyV3.fontWeight.semibold },
    xl: { ...typographyV3.fontSize.xl, fontWeight: typographyV3.fontWeight.semibold },
  },
  
  // Input text
  input: {
    sm: typographyV3.fontSize.sm,
    base: typographyV3.fontSize.base,
    lg: typographyV3.fontSize.lg,
  },
  
  // Label text
  label: {
    sm: { ...typographyV3.fontSize.sm, fontWeight: typographyV3.fontWeight.medium },
    base: { ...typographyV3.fontSize.base, fontWeight: typographyV3.fontWeight.medium },
  },
  
  // Caption/helper text
  caption: {
    xs: { ...typographyV3.fontSize.xs, color: 'rgba(0, 0, 0, 0.6)' },
    sm: { ...typographyV3.fontSize.sm, color: 'rgba(0, 0, 0, 0.6)' },
  },
  
  // Code text
  code: {
    xs: { ...typographyV3.fontSize.xs, fontFamily: typographyV3.fontFamily.mono },
    sm: { ...typographyV3.fontSize.sm, fontFamily: typographyV3.fontFamily.mono },
    base: { ...typographyV3.fontSize.base, fontFamily: typographyV3.fontFamily.mono },
  },
  
  // Modal text
  modal: {
    title: { ...typographyV3.fontSize['2xl'], fontWeight: typographyV3.fontWeight.semibold },
    description: { ...typographyV3.fontSize.base, lineHeight: '28px' },
    action: { ...typographyV3.fontSize.base, fontWeight: typographyV3.fontWeight.medium },
  },
} as const

// Spanish-first text styling
export const spanishTypography = {
  // Optimized for Spanish text characteristics
  body: {
    fontFamily: typographyV3.fontFamily.sans,
    fontSize: typographyV3.fontSize.base.size,
    lineHeight: '1.7', // Extra generous for Spanish diacritics
    letterSpacing: '0.01em', // Slight spacing for clarity
  },
  
  // Spanish headings with proper spacing
  heading: {
    fontFamily: typographyV3.fontFamily.display,
    letterSpacing: '-0.02em',
    lineHeight: '1.2',
  },
  
  // Spanish UI text
  ui: {
    fontFamily: typographyV3.fontFamily.sans,
    letterSpacing: '0.005em',
  },
} as const

// Responsive typography breakpoints
export const responsiveTypography = {
  // Mobile-first approach
  mobile: {
    h1: typographyV3.fontSize['3xl'],
    h2: typographyV3.fontSize['2xl'],
    h3: typographyV3.fontSize.xl,
    body: typographyV3.fontSize.base,
    small: typographyV3.fontSize.sm,
  },
  
  // Tablet and up
  tablet: {
    h1: typographyV3.fontSize['4xl'],
    h2: typographyV3.fontSize['3xl'],
    h3: typographyV3.fontSize['2xl'],
    body: typographyV3.fontSize.base,
    small: typographyV3.fontSize.sm,
  },
  
  // Desktop and up
  desktop: {
    h1: typographyV3.fontSize['5xl'],
    h2: typographyV3.fontSize['4xl'],
    h3: typographyV3.fontSize['3xl'],
    body: typographyV3.fontSize.lg,
    small: typographyV3.fontSize.base,
  },
  
  // Large screens
  large: {
    h1: typographyV3.fontSize['6xl'],
    h2: typographyV3.fontSize['5xl'],
    h3: typographyV3.fontSize['4xl'],
    body: typographyV3.fontSize.lg,
    small: typographyV3.fontSize.base,
  },
} as const

// Text color tokens for different states
export const textColors = {
  primary: 'rgba(0, 0, 0, 0.9)',
  secondary: 'rgba(0, 0, 0, 0.7)',
  tertiary: 'rgba(0, 0, 0, 0.5)',
  quaternary: 'rgba(0, 0, 0, 0.3)',
  disabled: 'rgba(0, 0, 0, 0.25)',
  
  // Semantic colors
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',
  
  // Interactive states
  link: '#1F6FEB',
  linkHover: '#1D4ED8',
  linkVisited: '#7C3AED',
  
  // On colored backgrounds
  onPrimary: 'rgba(255, 255, 255, 0.95)',
  onSecondary: 'rgba(255, 255, 255, 0.8)',
  onTertiary: 'rgba(255, 255, 255, 0.6)',
} as const